import { Autocomplete, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FC, HTMLAttributes, Key, memo } from 'react';
import { ShipperOption } from 'src/components/selectors/ShipperOption';
import { ShipperEntity } from 'src/entities/shipperEntity';

interface ShipperAutocompleteProps {
  label: string;
  id: string;
  shipperData: ShipperEntity[];
  selectedShipperId: number | null;
  selectedShipperName: string | null;
  onShipperIdChange: (shipperId: number | null) => void;
  onShipperNameChange: (shipperName: string | null) => void;
  loading?: boolean;
}

type ShipperOption = ShipperEntity & {
  label: string;
};

const ShipperAutocomplete: FC<ShipperAutocompleteProps> = memo(({
  label,
  id,
  shipperData,
  selectedShipperId,
  selectedShipperName,
  onShipperIdChange,
  onShipperNameChange,
  loading = false
}) => {
  const queryClient = useQueryClient();
  const deleteShipper = useMutation((shipperId: number) => axios.delete(`/api/v2/shippers/${shipperId}`), {
    onSuccess: async (_, shipperId) => {
      if (selectedShipperId === shipperId) {
        onShipperIdChange(null);
        onShipperNameChange(null);
      }
      await queryClient.invalidateQueries(['shippers']);
    },
  });

  const options: ShipperOption[] = shipperData.map((d) => ({
    ...d,
    label: d.name
  }));

  const deleteShipperOption = (option: ShipperOption) => {
    if (!window.confirm(`${option.name} を補完候補から削除します。よろしいですか？`)) return;
    deleteShipper.mutate(option.id);
  };

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={loading || deleteShipper.isLoading}
      value={selectedShipperName || ''}
      onChange={(_, value) => {
        if (typeof value === 'object' && value !== null) {
          // 選択肢から選択された場合
          onShipperIdChange(value.id);
          onShipperNameChange(value.name);
        } else if (typeof value === 'string') {
          // 文字列入力の場合
          const shipper = shipperData.find((it) => it.name === value);
          if (shipper) {
            // 既存の選択肢と一致する場合
            onShipperIdChange(shipper.id);
            onShipperNameChange(shipper.name);
          } else {
            // 新しい値の場合
            onShipperIdChange(null);
            onShipperNameChange(value);
          }
        } else {
          // null/undefinedの場合
          onShipperIdChange(null);
          onShipperNameChange(null);
        }
      }}
      onInputChange={(_, value) => {
        // 直接入力時の値を取得
        const shipper = shipperData.find((it) => it.name === value);
        if (shipper) {
          onShipperIdChange(shipper.id);
          onShipperNameChange(shipper.name);
        } else {
          onShipperIdChange(null);
          onShipperNameChange(value || null);
        }
      }}
      multiple={false}
      fullWidth
      renderOption={(props, option) => {
        const optionProps = { ...props } as HTMLAttributes<HTMLLIElement> & { key?: Key };
        const optionKey = optionProps.key;
        delete optionProps.key;

        return (
          <li key={optionKey} {...optionProps}>
            <ShipperOption option={option} disabled={deleteShipper.isLoading} onDelete={deleteShipperOption} />
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          margin="dense"
          type="text"
          fullWidth
          label={label}
          id={id}
          InputLabelProps={{ shrink: true }}
        />
      )}
    />
  );
});

export default ShipperAutocomplete;
